import { useEffect, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import {
    Card,
    CardBody,
} from 'reactstrap';

import useMailboxActions from '../../hooks/useMailboxActions';
import useMailboxAutoSync from '../../hooks/useMailboxAutoSync';
import useMailboxIntentPreview from '../../hooks/useMailboxIntentPreview';
import {
    getMailboxFolderPath,
    getMessageId,
    getThreadActionIds,
    SHOW_MAILBOX_ROW_CHECKBOXES,
} from '../../helpers';
import MessageIntentAnalysisModal from '../message-intent-analysis-modal';
import MessagePagination from './components/MessagePagination';
import MessageRow from './components/MessageRow';
import MessageSkeleton from './components/MessageSkeleton';
import MessageToolbar from './components/MessageToolbar';
import useMailboxFolderIntents from './hooks/useMailboxFolderIntents';
import useMailboxMessages from './hooks/useMailboxMessages';
import useMailboxRoleAccess from 'src/modules/mailer/hooks/useMailboxRoleAccess';

const MessageFolder = ({
    t,
    folder = 'inbox',
    showFlagFilters = false,
    partyMode = 'from',
}) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const {
        page,
        setPage,
        messages,
        total,
        totalPages,
        limit,
        isLoading,
    } = useMailboxMessages({
        t,
        folder,
        enableFlags: showFlagFilters,
    });
    const {
        isActing,
        isSyncing,
        updateFlags,
        trashMessages,
        restoreMessages,
        syncMailbox,
    } = useMailboxActions({ t });
    const { canSyncMailbox, canApplyIntents } = useMailboxRoleAccess();

    useMailboxAutoSync({
        enabled: folder === 'inbox' && canSyncMailbox,
        onSync: () => syncMailbox({ silent: true }),
    });

    useEffect(() => {
        setSelectedIds([]);
    }, [folder, page]);

    useEffect(() => {
        const visibleIds = new Set(
            messages.map((message) => getMessageId(message)).filter(Boolean).map(String)
        );

        setSelectedIds((current) => current.filter((id) => visibleIds.has(String(id))));
    }, [messages]);

    const {
        intents,
        applying,
        isApplying,
        applyIntent,
        getCases,
    } = useMailboxFolderIntents({
        t,
    });
    const preview = useMailboxIntentPreview({
        t,
        intents,
        applyCase: ({ messageId, intent, existingCases, analysis, message }) => (
            applyIntent(messageId, intent, existingCases, analysis, message)
        ),
    });

    const visibleIds = messages.map((message) => getMessageId(message)).filter(Boolean);
    const selectedIdSet = new Set(selectedIds.map(String));
    const allSelected = visibleIds.length > 0
        && visibleIds.every((id) => selectedIdSet.has(String(id)));
    const someSelected = selectedIds.length > 0 && !allSelected;

    const handleToggleSelect = (id, checked) => {
        const key = String(id);

        setSelectedIds((current) => {
            const isSelected = current.some((item) => String(item) === key);

            if (checked === true) {
                return isSelected ? current : [...current, id];
            }

            if (checked === false) {
                return isSelected
                    ? current.filter((item) => String(item) !== key)
                    : current;
            }

            return isSelected
                ? current.filter((item) => String(item) !== key)
                : [...current, id];
        });
    };

    const handleToggleSelectAll = (checked) => {
        setSelectedIds(checked ? visibleIds : []);
    };

    const asIdList = (ids) => (Array.isArray(ids) ? ids : [ids]).filter(Boolean);

    const expandThreadActionIds = (ids) => {
        const seen = new Set();
        const next = [];

        asIdList(ids).forEach((id) => {
            const message = messages.find((item) => String(getMessageId(item)) === String(id));
            const threadIds = message ? getThreadActionIds(message) : [id];

            threadIds.forEach((threadId) => {
                const key = String(threadId);

                if (seen.has(key)) {
                    return;
                }

                seen.add(key);
                next.push(threadId);
            });
        });

        return next;
    };

    const handleToggleStar = (ids, starred) => {
        updateFlags(asIdList(ids), { starred });
    };

    const handleToggleRead = (ids, unread) => {
        updateFlags(asIdList(ids), { unread });
    };

    const actionsBusy = isActing || isApplying || preview.isBusy;

    const handleBulkApplyIntent = (intent) => {
        preview.startPreviewForItems(
            selectedIds.map((id) => {
                const message = messages.find((item) => (
                    String(getMessageId(item)) === String(id)
                ));

                return {
                    messageId: id,
                    existingCases: getCases(message),
                    message,
                };
            }),
            intent
        );
    };

    if (isLoading && !messages.length) {
        return <MessageSkeleton />;
    }

    return (
        <Card>
            <CardBody>
                <MessageToolbar
                    folder={folder}
                    selectedCount={selectedIds.length}
                    intents={intents}
                    onBulkTrash={() => trashMessages(expandThreadActionIds(selectedIds))}
                    onBulkRestore={() => restoreMessages(expandThreadActionIds(selectedIds))}
                    onBulkRead={() => updateFlags(expandThreadActionIds(selectedIds), { unread: false })}
                    onBulkUnread={() => updateFlags(expandThreadActionIds(selectedIds), { unread: true })}
                    onBulkStar={() => updateFlags(expandThreadActionIds(selectedIds), { starred: true })}
                    onApplyIntent={handleBulkApplyIntent}
                    onSync={syncMailbox}
                    isActing={actionsBusy}
                    isSyncing={isSyncing}
                    showSync={canSyncMailbox}
                />

                {!messages.length ? (
                    <p className="text-muted mb-0">
                        {t('This folder is empty.')}
                    </p>
                ) : (
                    <ul className="mailbox-message-list">
                        {SHOW_MAILBOX_ROW_CHECKBOXES && canApplyIntents ? (
                            <li className="mailbox-message-row mailbox-message-row--header">
                                <label
                                    className="mailbox-message-row__check"
                                    onClick={(event) => event.stopPropagation()}
                                    onMouseDown={(event) => event.stopPropagation()}
                                >
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        disabled={actionsBusy || !visibleIds.length}
                                        ref={(element) => {
                                            if (element) {
                                                element.indeterminate = someSelected;
                                            }
                                        }}
                                        onChange={(event) => {
                                            handleToggleSelectAll(event.target.checked);
                                        }}
                                        aria-label={t('Select all')}
                                        title={t('Select all')}
                                    />
                                </label>
                            </li>
                        ) : null}
                        {messages.map((message) => {
                            const id = getMessageId(message);

                            return (
                                <MessageRow
                                    key={id}
                                    message={message}
                                    partyMode={partyMode}
                                    folder={folder}
                                    folderPath={getMailboxFolderPath(folder)}
                                    selected={selectedIdSet.has(String(id))}
                                    isActing={actionsBusy}
                                    intents={intents}
                                    cases={getCases(message)}
                                    applying={applying}
                                    onToggleSelect={handleToggleSelect}
                                    onToggleStar={handleToggleStar}
                                    onToggleRead={handleToggleRead}
                                    onTrash={(ids) => trashMessages(asIdList(ids))}
                                    onRestore={(ids) => restoreMessages(asIdList(ids))}
                                    onApplyIntent={(messageId, intent) => preview.startPreview({
                                        messageId,
                                        intent,
                                        existingCases: getCases(message),
                                        message,
                                    })}
                                    showAdminControls={canApplyIntents}
                                />
                            );
                        })}
                    </ul>
                )}

                <MessagePagination
                    page={page}
                    limit={limit}
                    total={total}
                    totalPages={totalPages}
                    count={messages.length}
                    onPageChange={setPage}
                />
            </CardBody>
            <MessageIntentAnalysisModal
                isOpen={preview.isOpen}
                session={preview.session}
                analysis={preview.analysis}
                isAnalyzing={preview.isAnalyzing}
                analyzingIntent={preview.analyzingIntent}
                analyzingProgress={preview.analyzingProgress}
                isApplying={preview.isApplying}
                onCancel={preview.handleCancel}
                onConfirm={preview.handleConfirm}
            />
        </Card>
    );
};

export default withNamespaces()(MessageFolder);
